import { useCallback, useEffect, useMemo, useState, type ComponentProps } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { ApiError, hasScope } from '@nyce/admin-kit/api';
import { Chip, Select } from '@nyce/admin-kit/ui';
import { SCOPES } from '../../api';
import { db, type DbOverview, type DbServerRef, type Job, type JobStatus } from '../../api/db';
import { useInterval } from '../../components/common';
import { ConfirmDialog as KitConfirmDialog } from '@nyce/admin-kit/console';

export interface DbScopes {
  read: boolean;
  backup: boolean;
  copy: boolean;
}

export const dbScopes = (scopes: readonly string[] | null | undefined): DbScopes => ({
  read: hasScope(scopes, SCOPES.dbRead),
  backup: hasScope(scopes, SCOPES.dbBackup),
  copy: hasScope(scopes, SCOPES.dbCopy),
});

/** May the caller start/cancel/schedule jobs of this kind? */
export const canRunKind = (s: DbScopes, kind: Job['kind']) => (kind === 'backup' ? s.backup : s.copy);

export type DbTab = 'overview' | 'sessions' | 'tables' | 'advice' | 'backups' | 'jobs' | 'copy' | 'schedules';

/** Page state kept in the URL query: tab, server, database, open job. */
export function useDbQuery() {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (patch: Partial<Record<'tab' | 'server' | 'db' | 'job', string | number | null>>, replace = false) =>
      setParams(
        (cur) => {
          const next = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === '') next.delete(k);
            else next.set(k, String(v));
          }
          return next;
        },
        { replace }
      ),
    [setParams]
  );
  const job = Number(params.get('job'));
  return {
    tab: (params.get('tab') as DbTab | null) ?? 'overview',
    server: params.get('server') ?? '',
    database: params.get('db') ?? '',
    job: Number.isInteger(job) && job > 0 ? job : null,
    set,
  };
}

/** Server errors in operator words. */
export function dbErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'You lack the permission for this action.';
    if (e.code === 'signing_unavailable')
      return 'Signed download links are not available on this deployment (the API has no key to sign GCS URLs). Use Live download on the Overview tab, or fetch the object from the GCS console.';
    if (e.code === 'protected_target')
      return 'The target server is protected and can never be a copy target.';
    if (e.code === 'confirmation_required') return 'Type the target database name exactly to confirm.';
    if (e.code === 'in_use') return 'Still used by a schedule.';
    if (e.code === 'invalid_cron') return `Invalid cron expression: ${e.message}`;
    if (e.code === 'unknown_database') return 'That database does not exist on the server.';
  }
  return (e as Error)?.message || String(e);
}

export function ServerPicker({
  servers,
  value,
  onChange,
  label = 'Server',
}: {
  servers: DbServerRef[];
  value: string;
  onChange: (id: string) => void;
  label?: string;
}) {
  return (
    <Select
      aria-label={label}
      value={value}
      placeholder="Pick a server"
      onChange={onChange}
      options={servers.map((s) => ({
        value: s.id,
        label: `${s.name || s.id}${s.protected ? ' (protected)' : ''}`,
        group: s.environment === 'production' ? 'Production' : 'Staging',
      }))}
    />
  );
}

export function DatabasePicker({
  databases,
  value,
  onChange,
  all,
  label = 'Database',
}: {
  databases: string[];
  value: string;
  onChange: (db: string) => void;
  /** Label of the "no filter" option; omitted = a database must be picked. */
  all?: string;
  label?: string;
}) {
  const options = databases.map((d) => ({ value: d, label: d }));
  if (value && !databases.includes(value)) options.unshift({ value, label: value });
  return (
    <Select
      aria-label={label}
      value={value}
      placeholder={all ? undefined : 'Pick a database'}
      onChange={onChange}
      options={all ? [{ value: '', label: all }, ...options] : options}
    />
  );
}

const overviewCache = new Map<string, Promise<DbOverview>>();
export const clearOverviewCache = () => {
  overviewCache.clear();
  namesCache.clear();
};

/** A server's overview, cached briefly so the database pickers of every tab share one call. */
export function loadOverview(serverId: string, fresh = false): Promise<DbOverview> {
  const hit = overviewCache.get(serverId);
  if (hit && !fresh) return hit;
  const p = db.overview(serverId);
  overviewCache.set(serverId, p);
  p.then(
    () => setTimeout(() => overviewCache.get(serverId) === p && overviewCache.delete(serverId), 30_000),
    () => overviewCache.get(serverId) === p && overviewCache.delete(serverId)
  );
  return p;
}

const namesCache = new Map<string, Promise<string[]>>();

/** Database names of a server (`GET /db/servers/{id}/databases`, cached 30 s), for pickers; [] while loading or without a server. */
export function useServerDatabases(serverId: string): { names: string[]; error: string | null } {
  const [state, setState] = useState<{ id: string; names: string[]; error: string | null }>({
    id: '',
    names: [],
    error: null,
  });
  useEffect(() => {
    if (!serverId) return;
    let live = true;
    let p = namesCache.get(serverId);
    if (!p) {
      const q = db.databases(serverId);
      namesCache.set(serverId, q);
      q.then(
        () => setTimeout(() => namesCache.get(serverId) === q && namesCache.delete(serverId), 30_000),
        () => namesCache.get(serverId) === q && namesCache.delete(serverId)
      );
      p = q;
    }
    p.then(
      (names) => live && setState({ id: serverId, names, error: null }),
      (e) => live && setState({ id: serverId, names: [], error: dbErrorText(e) })
    );
    return () => {
      live = false;
    };
  }, [serverId]);
  return state.id === serverId ? state : { names: [], error: null };
}

const STATUS_CLASS: Record<JobStatus, string> = {
  queued: 'dbx-st-queued',
  running: 'dbx-st-running',
  succeeded: 'dbx-st-ok',
  failed: 'dbx-st-failed',
  cancelled: 'dbx-st-off',
};

export function StatusChip({ job }: { job: Pick<Job, 'status' | 'progress'> }) {
  const pct = job.status === 'running' && job.progress != null ? ` ${Math.round(job.progress)}%` : '';
  return (
    <Chip size="sm" className={`dbx-status ${STATUS_CLASS[job.status]}`} data-status={job.status}>
      {job.status}
      {pct}
    </Chip>
  );
}

/** A horizontal bar: `parts` are stacked fractions of `max`. */
export function Bar({
  parts,
  max,
  label,
  tone,
}: {
  parts: { value: number; className?: string; title?: string }[];
  max: number;
  label?: string;
  tone?: 'warn' | 'bad';
}) {
  return (
    <div className={`dbx-bar${tone ? ` dbx-bar-${tone}` : ''}`} role="img" aria-label={label} title={label}>
      {parts.map((p, i) => (
        <span
          key={i}
          className={['dbx-bar-fill', p.className].filter(Boolean).join(' ')}
          title={p.title}
          style={{ width: `${max > 0 ? Math.min(100, Math.max(0, (p.value / max) * 100)) : 0}%` }}
        />
      ))}
    </div>
  );
}

/** A confirmation dialog for destructive or costly actions (the kit's, with the database errors in words). */
export function ConfirmDialog(props: Omit<ComponentProps<typeof KitConfirmDialog>, 'formatError'>) {
  return <KitConfirmDialog {...props} formatError={dbErrorText} />;
}

export function ProtectedChip() {
  return (
    <Chip size="sm" className="oph-lock" icon={<Lock size={11} />}>
      protected
    </Chip>
  );
}

export { startDownload } from '@nyce/admin-kit/api';

export { useInterval };

/** Current time, ticking every `ms` (durations of running things). */
export function useNow(ms = 1000, on = true) {
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), ms, on);
  return now;
}

export const serverName = (servers: DbServerRef[], id: string | null | undefined) =>
  id ? (servers.find((s) => s.id === id)?.name ?? id) : '';

/** `server/db` in one line. */
export function Target({
  servers,
  server,
  database,
}: {
  servers: DbServerRef[];
  server: string;
  database: string;
}) {
  return (
    <span className="dbx-target">
      <span className="dbx-muted">{serverName(servers, server)}/</span>
      <wbr />
      {database}
    </span>
  );
}

export const useSortedServers = (servers: DbServerRef[] | null) =>
  useMemo(
    () =>
      [...(servers ?? [])].sort(
        (a, b) =>
          (a.environment === b.environment ? 0 : a.environment === 'production' ? -1 : 1) ||
          (a.name || a.id).localeCompare(b.name || b.id)
      ),
    [servers]
  );
